import { convertBlobToBase64, openPrinterWithUrl } from '@/utils/file';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Document, pdf } from '@react-pdf/renderer';
import { Col, Form, Row } from 'antd';
import { Fragment } from 'react';
import { useState } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  checkLDClaimXPOExportInProgress,
  createLDClaimReset,
  sendLDClaimEmailToCarrierInProgress,
} from '../../../../../redux/Claims/LossAndDamage/LossAndDamageClaimsActions';
import { setAppLoader } from '../../../../../redux/common/CommonActions';
import { downloadBlobFile } from '../../../../../utilz';
import { routeFileSelector } from '../../../../../utilz/i18n';
import Button from '../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import CommonModal from '../../../../components/Modals/CommonModal';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import LADClaimPDF from '../../../Print/Claims/LossAndDamage/LADClaimPDF';
import TMSFiledCard from '../../../common/components/TMSFiledCard';
import { PrintFooter } from '../../common/PrintFooter';
import { useGetLADPrintForm } from '../../hooks';
import LADPrintClaim from '../Prints/LADPrintClaim';
import LDSendToCarrierForm from '../Prints/LDSendToCarrierForm';

const LADDrawerHeader = ({ claimData }) => {
  const dispatch = useDispatch();
  const [printModal, setPrintModal] = useState(false);
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [isXpoExport, setIsXpoExport] = useState(false);
  const [exportForm] = Form.useForm();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const { claimDetails, checkXpoExport, createClaim } = useSelector((state) => state.lossAndDamage);

  const {
    data: claimDetailsBatch,
    refetch: refetchClaimDetailsBatch,
    isLoading: isClaimDetailsBatchLoading,
  } = useGetLADPrintForm({
    ClaimKeys: String(claimData?.claimKey || ''),
    enabled: false,
  });

  const handleGeneratePDF = async () => {
    try {
      dispatch(setAppLoader(true));
      const fileName = 'Claim Details.pdf';
      const blob = await pdf(<PDFdocument claimData={claimDetailsBatch?.[0]} />).toBlob();
      downloadBlobFile(blob, fileName);
      dispatch(setAppLoader(false));
    } catch (error) {
      console.log(error);
      dispatch(setAppLoader(false));
    }
  };

  const handlePrintForm = () => {
    refetchClaimDetailsBatch();
    setPrintModal(true);
  };

  const handleSendEmailToCarrier = async (payload) => {
    try {
      dispatch(setAppLoader(true));
      if (payload.attachments[0].isAttached) {
        await refetchClaimDetailsBatch();
        const blob = await pdf(<PDFdocument claimData={claimDetailsBatch?.[0]} />).toBlob();
        const fileBase64 = await convertBlobToBase64(blob);
        payload.attachments[0].fileContent = String(fileBase64).split(',')[1];
      }
      payload.handleDone = () => {
        setSendEmailModal(false);
        setIsXpoExport(false);
      };
      payload.isXpoExport = isXpoExport;
      dispatch(sendLDClaimEmailToCarrierInProgress(payload));
    } catch (error) {
      console.log(error);
      dispatch(setAppLoader(false));
    }
  };

  const handleXpoExportClick = () => {
    const handleDone = () => {
      setSendEmailModal(true);
      setIsXpoExport(true);
    };
    const payload = { claimKey: claimDetails.data?.claimKey, handleDone };
    dispatch(checkLDClaimXPOExportInProgress(payload));
  };

  const handleDefaultEmailSendDialogueCancel = () => {
    dispatch(createLDClaimReset());
  };

  const handlePrint = async () => {
    let pdfUrl = '';
    try {
      const blob = await pdf(<PDFdocument claimData={claimDetailsBatch?.[0]} />).toBlob();
      pdfUrl = URL.createObjectURL(blob);
      await openPrinterWithUrl(pdfUrl, 'claims-pdf-print-iframe');
    } catch {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        message: t('translation:default.fields.printError'),
      });
    } finally {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    }
  };

  return (
    <Fragment>
      <Row justify="space-between" className="align-items-center" gutter={30}>
        <Col>
          <Row gutter={30}>
            <Col>
              <TMSFiledCard labelClass="text-light f-16" title="Claim Number">
                <PrimaryLabel normal className="f-20 text-white">
                  {claimData?.claimNumber}
                </PrimaryLabel>
              </TMSFiledCard>
            </Col>
            <Col>
              <TMSFiledCard labelClass="text-light f-16" title="Currency">
                <PrimaryLabel normal className="f-20 text-white">
                  {claimData?.currencyCode}
                </PrimaryLabel>
              </TMSFiledCard>
            </Col>
          </Row>
        </Col>
        <Col className="pr-30">
          {claimData?.claimKey ? (
            <Row gutter={15}>
              <Col>
                <Button type="default" invertMode onClick={handlePrintForm}>
                  Print Form
                </Button>
              </Col>
              <Col>
                <Button type="default" invertMode onClick={() => setSendEmailModal(true)}>
                  Email To Transportation Provider
                </Button>
              </Col>
              <Col>
                <Button type="default" invertMode onClick={handleXpoExportClick} loading={checkXpoExport.progressing}>
                  XPO Export
                </Button>
              </Col>
            </Row>
          ) : (
            <></>
          )}
        </Col>
      </Row>
      <CommonModal
        className="vw-80"
        open={printModal}
        title="Loss And Damage Print"
        okText="Print"
        onClose={() => setPrintModal(false)}
        customFooter={<PrintFooter handlePrint={handlePrint} generatePDF={handleGeneratePDF} setOpen={setPrintModal} />}
        afterOpenChange={(val) => {
          if (!val) {
            exportForm.resetFields();
          }
        }}
      >
        <div className="h-100 custom-scroll-bar">
          {isClaimDetailsBatchLoading ? (
            <ContentSpinner size="large" />
          ) : (
            <LADPrintClaim claimData={claimDetailsBatch ? claimDetailsBatch?.[0] : {}} />
          )}
        </div>
      </CommonModal>
      <CommonModal
        className="vh-65 custom-claim-email-send-popup"
        open={sendEmailModal}
        title="Email To Transportation Provider"
        customFooter={<></>}
        onClose={() => setSendEmailModal(false)}
        destroyOnClose
      >
        <LDSendToCarrierForm
          handleSendEmailToCarrier={handleSendEmailToCarrier}
          setSendEmailModal={setSendEmailModal}
        />
      </CommonModal>
      <ConfirmationModal
        className="w-auto"
        title="Alert"
        open={createClaim.success && claimDetails.success && !claimDetails.progressing}
        description={
          <>
            {t('translation:claims.successFullyCreated')}. {t('translation:default.fields.claimNumber')} :{' '}
            {claimDetails?.data?.claimNumber} <br />
            {t('translation:claims.sendEmailToTransportationProviderConfirmation')}
          </>
        }
        onOk={() => {
          setSendEmailModal(true);
          handleDefaultEmailSendDialogueCancel();
        }}
        onClose={handleDefaultEmailSendDialogueCancel}
        onCancel={handleDefaultEmailSendDialogueCancel}
      />
    </Fragment>
  );
};

export default LADDrawerHeader;

const PDFdocument = ({ claimData }) => {
  return (
    <Document>
      <LADClaimPDF claimDetails={claimData} />
    </Document>
  );
};
